'use client'

import { Button } from '@payloadcms/ui'
import React from 'react'
import type { QuestionGraderProps } from './types'
import { renderCorrectAnswers, renderStudentAnswer } from './utils'

export const QuestionGrader: React.FC<QuestionGraderProps> = ({
  question,
  index,
  test,
  gradingDetail,
  studentAnswer,
  feedback,
  onFeedbackChange,
  onManualGrade,
}) => {
  const [customPoints, setCustomPoints] = React.useState<string>(
    gradingDetail?.points?.toString() || '',
  )

  if (!question.id) return null

  // Check if question should be auto-graded based on type AND test.autoGrade setting
  const isAutoGraded =
    (question.type === 'radio' || question.type === 'checkbox') && test.autoGrade !== false
  const showFeedback = ['textarea', 'file'].includes(question.type)

  // Determine grading status
  const isGraded =
    gradingDetail && (gradingDetail.isCorrect !== null || gradingDetail.isCorrect !== undefined)
  const needsGrading = !isAutoGraded && !isGraded

  const handleCustomPointsGrade = () => {
    const pointsValue = parseFloat(customPoints)
    if (isNaN(pointsValue) || pointsValue < 0) {
      alert('Please enter a valid non-negative number for points')
      return
    }
    if (pointsValue > question.points) {
      alert(`Points cannot exceed the maximum (${question.points})`)
      return
    }
    onManualGrade(
      question.id!,
      pointsValue > 0,
      pointsValue,
      feedback !== undefined ? feedback : gradingDetail?.feedback?.text,
    )
  }

  // Determine border and background colors based on status
  const getBorderColor = () => {
    if (needsGrading) return 'border-[var(--theme-warning-200)]'
    if (gradingDetail?.isCorrect === true) return 'border-[var(--theme-success-200)]'
    if (gradingDetail?.isCorrect === false) return 'border-[var(--theme-error-200)]'
    return 'border-[var(--theme-elevation-200)]'
  }

  const getBackgroundColor = () => {
    if (needsGrading) return 'bg-[var(--theme-warning-50)]'
    if (gradingDetail?.isCorrect === true) return 'bg-[var(--theme-success-50)]'
    if (gradingDetail?.isCorrect === false) return 'bg-[var(--theme-error-50)]'
    return 'bg-[var(--theme-elevation-50)]'
  }

  return (
    <div
      key={question.id}
      className={`p-6 border-2 rounded-lg transition-all ${getBorderColor()} ${getBackgroundColor()}`}
    >
      <div className="flex items-start justify-between mb-6 gap-3">
        <div className="flex-1">
          <h4 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
            Question {index + 1}: {question.title}
          </h4>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="px-2 py-1 text-xs font-medium rounded"
              style={{
                backgroundColor: 'var(--theme-elevation-150)',
                color: 'var(--theme-elevation-700)',
              }}
            >
              {question.type.charAt(0).toUpperCase() + question.type.slice(1)}
            </span>
            <span
              className="px-2 py-1 text-xs font-medium rounded"
              style={{
                backgroundColor: 'var(--theme-success-100)',
                color: 'var(--theme-success-700)',
              }}
            >
              {question.points} {question.points === 1 ? 'point' : 'points'}
            </span>
          </div>
        </div>
        {/* Status Badge */}
        {needsGrading ? (
          <span
            className="px-3 py-1.5 text-xs font-bold rounded-full shadow-sm whitespace-nowrap"
            style={{ backgroundColor: 'var(--theme-warning-600)', color: 'var(--color-base-0)' }}
          >
            ⚠️ NEEDS GRADING
          </span>
        ) : isGraded && gradingDetail?.isCorrect === true ? (
          <span
            className="px-3 py-1.5 text-xs font-bold rounded-full shadow-sm whitespace-nowrap"
            style={{ backgroundColor: 'var(--theme-success-600)', color: 'var(--color-base-0)' }}
          >
            ✓ CORRECT
          </span>
        ) : isGraded && gradingDetail?.isCorrect === false ? (
          <span
            className="px-3 py-1.5 text-xs font-bold rounded-full shadow-sm whitespace-nowrap"
            style={{ backgroundColor: 'var(--theme-error-600)', color: 'var(--color-base-0)' }}
          >
            ✗ INCORRECT
          </span>
        ) : isAutoGraded ? (
          <span
            className="px-3 py-1.5 text-xs font-bold rounded-full shadow-sm whitespace-nowrap"
            style={{ backgroundColor: 'var(--theme-success-500)', color: 'var(--color-base-0)' }}
          >
            ⚡ AUTO-GRADED
          </span>
        ) : null}
      </div>

      {/* Student's Answer */}
      <div className="mb-4">
        <h5 className="mb-3 font-medium" style={{ color: 'var(--theme-text)' }}>
          Student&apos;s Answer:
        </h5>
        {renderStudentAnswer(question, studentAnswer)}
      </div>

      {/* Show correct answers for radio/checkbox */}
      {renderCorrectAnswers(question)}

      {/* Grading Section */}
      <div
        className={`p-5 mt-6 border-2 rounded-lg ${needsGrading ? 'border-[var(--theme-warning-150)] bg-[var(--theme-warning-50)]' : 'border-[var(--theme-elevation-200)] bg-[var(--theme-elevation-100)]'}`}
      >
        <div className="flex items-center justify-between mb-4">
          <h5 className="font-semibold text-base" style={{ color: 'var(--theme-text)' }}>
            Grading
          </h5>
          <div className="text-sm font-semibold" style={{ color: 'var(--theme-elevation-700)' }}>
            <span
              style={{
                color:
                  gradingDetail?.points === question.points
                    ? 'var(--theme-success-700)'
                    : (gradingDetail?.points ?? 0) > 0
                      ? 'var(--theme-success-600)'
                      : 'var(--theme-error-600)',
              }}
            >
              {gradingDetail?.points ?? 0}
            </span>
            <span style={{ color: 'var(--theme-elevation-500)' }}> / {question.points}</span>
          </div>
        </div>

        {isAutoGraded ? (
          // Auto-graded
          <div
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-elevation-0)',
              borderColor: 'var(--theme-elevation-200)',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span
                  className="px-4 py-2 rounded-lg text-sm font-bold"
                  style={{
                    backgroundColor:
                      gradingDetail?.isCorrect === true
                        ? 'var(--theme-success-100)'
                        : gradingDetail?.isCorrect === false
                          ? 'var(--theme-error-100)'
                          : 'var(--theme-elevation-100)',
                    color:
                      gradingDetail?.isCorrect === true
                        ? 'var(--theme-success-700)'
                        : gradingDetail?.isCorrect === false
                          ? 'var(--theme-error-700)'
                          : 'var(--theme-elevation-700)',
                  }}
                >
                  {gradingDetail?.isCorrect === true
                    ? '✓ Correct'
                    : gradingDetail?.isCorrect === false
                      ? '✗ Incorrect'
                      : 'Not graded'}
                </span>
                <span className="text-sm" style={{ color: 'var(--theme-elevation-600)' }}>
                  (Auto-graded)
                </span>
              </div>
            </div>
          </div>
        ) : (
          // Manual grading for text, textarea, code, file
          <div className="space-y-4">
            {/* Quick action buttons */}
            <div
              className="p-4 rounded-lg border"
              style={{
                backgroundColor: 'var(--theme-elevation-0)',
                borderColor: 'var(--theme-elevation-200)',
              }}
            >
              <div
                className="text-xs font-semibold uppercase tracking-wide mb-3"
                style={{ color: 'var(--theme-elevation-600)' }}
              >
                Quick Grade
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={() =>
                    onManualGrade(
                      question.id!,
                      true,
                      question.points,
                      feedback !== undefined ? feedback : gradingDetail?.feedback?.text,
                    )
                  }
                  buttonStyle={
                    gradingDetail?.isCorrect === true && gradingDetail?.points === question.points
                      ? 'primary'
                      : 'secondary'
                  }
                  size="medium"
                  margin={false}
                >
                  ✓ Full Points ({question.points})
                </Button>
                <Button
                  onClick={() =>
                    onManualGrade(
                      question.id!,
                      false,
                      0,
                      feedback !== undefined ? feedback : gradingDetail?.feedback?.text,
                    )
                  }
                  buttonStyle={gradingDetail?.isCorrect === false ? 'primary' : 'secondary'}
                  size="medium"
                  margin={false}
                >
                  ✗ No Points (0)
                </Button>
              </div>
            </div>

            {/* Custom points input */}
            <div
              className="p-4 rounded-lg border"
              style={{
                backgroundColor: 'var(--theme-elevation-0)',
                borderColor: 'var(--theme-elevation-200)',
              }}
            >
              <div
                className="text-xs font-semibold uppercase tracking-wide mb-3"
                style={{ color: 'var(--theme-elevation-600)' }}
              >
                Custom Points
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  id={`custom-points-${question.id}`}
                  type="number"
                  min="0"
                  max={question.points}
                  step="0.5"
                  value={customPoints}
                  onChange={(e) => setCustomPoints(e.target.value)}
                  placeholder={`0 - ${question.points}`}
                  className="w-32 px-3 py-2 border rounded-lg text-sm font-medium focus:ring-2 focus:border-blue-500"
                  style={{
                    backgroundColor: 'var(--theme-input-bg)',
                    borderColor: 'var(--theme-border-color)',
                    color: 'var(--theme-text)',
                  }}
                />
                <span
                  className="text-sm font-medium"
                  style={{ color: 'var(--theme-elevation-600)' }}
                >
                  / {question.points}
                </span>
                <Button onClick={handleCustomPointsGrade} size="medium" margin={false}>
                  Apply Points
                </Button>
              </div>
            </div>

            {/* Feedback section */}
            {showFeedback && (
              <div
                className="p-4 rounded-lg border"
                style={{
                  backgroundColor: 'var(--theme-elevation-0)',
                  borderColor: 'var(--theme-elevation-200)',
                }}
              >
                <label
                  htmlFor={`feedback-${question.id}`}
                  className="text-xs font-semibold uppercase tracking-wide mb-3 block"
                  style={{ color: 'var(--theme-elevation-600)' }}
                >
                  Mentor Feedback (Optional)
                </label>
                <textarea
                  id={`feedback-${question.id}`}
                  value={feedback !== undefined ? feedback : gradingDetail?.feedback?.text || ''}
                  onChange={(e) => onFeedbackChange(question.id!, e.target.value)}
                  placeholder="Enter constructive feedback for the student..."
                  className="w-full p-3 border rounded-lg text-base font-sans min-h-[100px] resize-y focus:ring-2 focus:border-blue-500"
                  style={{
                    backgroundColor: 'var(--theme-input-bg)',
                    borderColor: 'var(--theme-border-color)',
                    color: 'var(--theme-text)',
                  }}
                />
              </div>
            )}

            {/* Status display */}
            {gradingDetail?.manuallyGraded && (
              <div className="flex items-center gap-2 text-sm">
                <span
                  className="px-3 py-1.5 rounded-lg font-medium"
                  style={{
                    backgroundColor: 'var(--theme-success-100)',
                    color: 'var(--theme-success-700)',
                  }}
                >
                  ✓ Manually Graded
                </span>
                {gradingDetail?.feedback && (
                  <span style={{ color: 'var(--theme-elevation-600)' }}>• Feedback provided</span>
                )}
              </div>
            )}

            {/* Saved feedback display */}
            {gradingDetail?.feedback && (
              <div
                className="p-4 rounded-lg border-l-4"
                style={{
                  backgroundColor: 'var(--theme-success-50)',
                  borderLeftColor: 'var(--theme-success-500)',
                }}
              >
                <div
                  className="text-sm font-semibold mb-2"
                  style={{ color: 'var(--theme-success-800)' }}
                >
                  Saved Mentor Feedback:
                </div>
                <div
                  className="text-sm whitespace-pre-wrap mb-1"
                  style={{ color: 'var(--theme-success-700)' }}
                >
                  {gradingDetail.feedback?.text}
                </div>
                {gradingDetail.feedback?.mentor && (
                  <div
                    className="text-xs font-medium"
                    style={{ color: 'var(--theme-elevation-600)' }}
                  >
                    — {gradingDetail.feedback.mentor.name || 'Unknown Mentor'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
